import {
	KYCFileType,
	GetInvestmentDetailsRes,
	KycWorkflowSettingResponseDto,
	SubjectAction,
} from '@dealstack/shared/lib/generated';
import { GreyBox } from '@dealstack/frontend/shared/components/common/GreyBox';

import { AlertTitle, Alert } from '@dealstack/frontend/shared/components/Alert/Alert';
import { FormCheckbox } from '@dealstack/frontend/shared/components/forms/FormCheckbox/FormCheckbox';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { KycInstructionBar } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/KycInstructions/KycInstructionBar';
import { ProofFileBox } from '@dealstack/frontend/investor/pages/Investment/common/ProofFileBox/ProofFileBox';
import { ProofFileBoxWrapper } from '@dealstack/frontend/investor/pages/Investment/common/ProofFileBox/styled';
import { Button, Flex, Text, Title } from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import styled, { css } from 'styled-components';

import {
	PIV_PROOFS_FORM_FIELD,
	useInvestmentLeavePIVProofController,
} from './useInvestmentLeavePIVProofController';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { isKycRejected } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import { CertifiableProofUpload } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/CertifiableProofUpload';
import { ProofCertificationStatus } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/ProofCertificationStatus';
import { isCertificationRequiredFor } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/CertifiableProof.utils';

interface Props {
	className?: string;
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
	investmentDetails: GetInvestmentDetailsRes | undefined;
	kycSetting?: KycWorkflowSettingResponseDto;
}

const InvestmentLeavePIVProof = ({
	className,
	setIsContinueDisabled,
	kycSetting,
	investmentDetails,
}: Props) => {
	const tInvestor = useTranslationInvestor();

	const isCertifiedCopiesRequested =
		kycSetting?.isProofOfInvestmentVehicleCertificationRequired ?? false;

	const {
		proofFilesDetails,
		uploadFileRequest,
		refetchProofs,
		onDelete,
		onConfirm,
		onResetAll,
		onFileDrop,
		pivProofsForm,
	} = useInvestmentLeavePIVProofController({ setIsContinueDisabled, investmentDetails });

	const inheritedFiles = proofFilesDetails?.filter((f) => f.subjectAction != null) ?? [];
	const hasInheritedItems = inheritedFiles.length > 0;
	const hasUnconfirmedInherited = inheritedFiles.some(
		(f) => f.subjectAction === SubjectAction.NOT_YET_ACTED
	);
	const hasAnyFiles = (proofFilesDetails?.length ?? 0) > 0;
	const showReset = hasAnyFiles && !hasUnconfirmedInherited;

	return (
		<div className={className}>
			<Text>{tInvestor('investment.join.kyc.piv_proofs.descr')}</Text>

			{hasInheritedItems && (
				<Flex direction="column" gap="0.25rem">
					<Text fw={700}>{tInvestor('investment.join.kyc.inherited.title')}</Text>
					<Text size="sm" c="dark.0">
						{tInvestor('investment.join.kyc.inherited.description')}
					</Text>
				</Flex>
			)}

			<Alert state="info" size="s">
				<AlertTitle>{tInvestor('investment.join.kyc.piv_proofs.remember')}</AlertTitle>
			</Alert>

			<GreyBox>
				<Flex direction="column" gap="1rem">
					<div>
						<Title order={5} sx={{ marginBottom: '0.25rem' }}>
							{tInvestor('investment.join.kyc.piv_proofs.checklist')}
						</Title>
						<Text fw={600}>
							{tInvestor('investment.join.kyc.piv_proofs.confirm_you_uploaded')}
						</Text>
					</div>
					<FormProvider {...pivProofsForm}>
						<FormCheckbox
							name={PIV_PROOFS_FORM_FIELD.SHARE_REGISTER}
							label={tInvestor('investment.join.kyc.piv_proofs.share_register')}
						/>
						<FormCheckbox
							name={PIV_PROOFS_FORM_FIELD.CERTIFICATE_OF_REGISTRATION}
							label={tInvestor('investment.join.kyc.piv_proofs.certificate_register')}
						/>
						<FormCheckbox
							name={PIV_PROOFS_FORM_FIELD.ARTICLES_OF_ASSOCIATION}
							label={tInvestor('investment.join.kyc.piv_proofs.articles_association')}
						/>
						<FormCheckbox
							name={PIV_PROOFS_FORM_FIELD.SIGNATORY_POWER}
							label={tInvestor('investment.join.kyc.piv_proofs.sign_power')}
						/>
					</FormProvider>
				</Flex>
			</GreyBox>

			<Flex
				direction="column"
				gap="0.5rem"
				sx={{
					padding: '0.5rem',
					border: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
					borderRadius: '4px',
				}}
			>
				{!!proofFilesDetails?.length && (
					<ListWrapper
						isSingleColumn={
							proofFilesDetails.some(({ status }) => isKycRejected(status)) ||
							!!hasInheritedItems
						}
					>
						{proofFilesDetails.map((proofFile) => (
							<ProofFileBox
								key={proofFile.fileId}
								fileName={proofFile.fileName}
								fileId={proofFile.fileId}
								onDelete={onDelete(proofFile.id)}
								onConfirm={onConfirm(proofFile.id)}
								subjectAction={proofFile.subjectAction}
								date={proofFile.createdAt}
								rejectionComment={proofFile.rejectionComment}
								isRejected={isKycRejected(proofFile.status)}
								certificationSlot={
									<ProofCertificationStatus
										kycFile={proofFile}
										certificationRequired={isCertificationRequiredFor(
											kycSetting,
											KYCFileType.INVESTMENT_VEHICLE
										)}
										isRejected={isKycRejected(proofFile.status)}
										onChanged={refetchProofs}
									/>
								}
							/>
						))}
					</ListWrapper>
				)}

				{showReset && (
					<Flex>
						<Button
							size="sm"
							variant="outline"
							color="dark"
							onClick={onResetAll}
							data-testid={
								INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_INHERITED_DISMISS_BUTTON
							}
						>
							{tInvestor('investment.join.kyc.inherited.reset')}
						</Button>
					</Flex>
				)}

				{!hasInheritedItems && (
					<CertifiableProofUpload
						certificationRequired={isCertificationRequiredFor(
							kycSetting,
							KYCFileType.INVESTMENT_VEHICLE
						)}
						onDropFile={onFileDrop}
						size="STD"
						loading={uploadFileRequest.isPending}
						multiple
					/>
				)}
			</Flex>

			<KycInstructionBar isCertifiedCopiesOfDocumentsRequested={isCertifiedCopiesRequested} />
		</div>
	);
};

const ListWrapper = styled.div<{ isSingleColumn: boolean }>`
	${(props) =>
		props.isSingleColumn
			? css`
					display: flex;
					flex-direction: column;
					gap: 0.5rem;
				`
			: css`
					display: grid;
					grid-template-columns: 1fr 1fr;
					grid-auto-rows: 1fr;
					grid-column-gap: 0.5rem;
					grid-row-gap: 0.5rem;

					${ProofFileBoxWrapper} > div:first-of-type {
						box-sizing: border-box;
						height: 100%;
					}
				`}
`;

const Styled = styled(InvestmentLeavePIVProof)`
	max-width: 40rem;
	display: flex;
	flex-direction: column;
	gap: 2rem;

	${Alert} {
		background-color: ${(props) => props.theme.v3.colors.gray[0]};
	}
`;

export { Styled as InvestmentLeavePIVProof };
